/**
 * System Health Panel — operational dashboard for the platform's runtime
 * subsystems. Polls /api/admin/system-health every 10s and renders a
 * green/amber/red badge per subsystem (Mongo, WebSockets, Janitor,
 * Dispatcher, Google APIs, recent errors).
 *
 * Includes a one-click "Copy snapshot" so an operator can paste the entire
 * health JSON into a support ticket or chat without re-typing fields.
 *
 * Every interactive element + every critical readout carries a data-testid
 * so the E2E suite can assert state without DOM-sniffing.
 */
import React, { useEffect, useState, useRef } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { BuildStamp } from "../../components/BuildStamp";
import {
  Database, Wifi, Repeat, Send, Map as MapIcon, AlertTriangle,
  RefreshCw, Copy, Clock, Mail, LocateFixed,
} from "lucide-react";

const POLL_MS = 10000;


// ─── Helpers ──────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  green: { dot: "#10B981", bg: "#ECFDF5", border: "#10B981", text: "#065F46" },
  amber: { dot: "#F59E0B", bg: "#FFFBEB", border: "#FFC107", text: "#92400E" },
  red:   { dot: "#EF4444", bg: "#FEF2F2", border: "#EF4444", text: "#991B1B" },
  unknown: { dot: "#9CA3AF", bg: "#F3F4F6", border: "#D1D5DB", text: "#374151" },
};

const STATUS_LABEL = {
  green: "Healthy", amber: "Degraded", red: "Critical", unknown: "Unknown",
};

const formatUptime = (s) => {
  if (s == null) return "—";
  const sec = Math.round(s);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ${sec % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const formatAgo = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms)) return iso;
  if (ms < 1000) return "just now";
  if (ms < 60000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m ago`;
  return `${Math.round(ms / 3600000)}h ago`;
};


// ─── Sub-components ───────────────────────────────────────────────────────

const StatusDot = ({ status, size = "w-3 h-3" }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <span
      className={`${size} rounded-full inline-block`}
      style={{ backgroundColor: c.dot }}
      data-testid={`status-dot-${status}`}
    />
  );
};

const SubsystemCard = ({ id, title, icon: Icon, status, children }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <div
      className="border-2 bg-white p-4"
      style={{ borderColor: c.border }}
      data-testid={`subsystem-${id}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-foreground" strokeWidth={2.5} />
        <div className="label-mini text-foreground font-bold">{title}</div>
        <span
          className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
          style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}` }}
          data-testid={`subsystem-${id}-status`}
        >
          <StatusDot status={status} size="w-2 h-2" />
          {STATUS_LABEL[status] || status}
        </span>
      </div>
      <div className="text-xs space-y-1 font-mono-tab">{children}</div>
    </div>
  );
};

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-muted-foreground uppercase text-[10px] tracking-wider">{label}</span>
    <span className="font-bold text-foreground truncate text-right" data-testid={testid}>
      {value ?? "—"}
    </span>
  </div>
);


// ─── Geocoding health (Voice line) — on-demand self-test ──────────────────
// Runs /api/admin/voice/geocode-selftest on click (NOT on the 10s poll — it
// makes a live Google request). Read-only, admin-only; never shows the key
// value, only which env var supplies it.
const geoStatus = (r) => {
  if (!r) return "unknown";                                   // grey — not run
  if (r.google_direct_ok && !r.using_browser_key_fallback) return "green";
  if (!r.resolved_ok) return "red";                           // geocoding unavailable
  return "amber";                                             // browser-key or fallback provider
};

const GeocodingHealthCard = () => {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);

  const runTest = async () => {
    setRunning(true);
    setErr(null);
    try {
      const res = await api.get("/admin/voice/geocode-selftest");
      setResult(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "test failed");
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  const status = geoStatus(result);
  return (
    <SubsystemCard id="geocoding" title="Geocoding (Voice line)" icon={LocateFixed} status={status}>
      <Row
        label="Key source"
        value={result ? (result.key_source || "NONE (Nominatim only)") : "—"}
        testid="geocoding-key-source"
      />
      <Row
        label="Server key"
        value={result ? (result.using_browser_key_fallback ? "BROWSER KEY ⚠" : (result.key_configured ? "yes" : "no")) : "—"}
        testid="geocoding-server-key"
      />
      <Row
        label="Google responding"
        value={result ? (result.google_direct_ok ? "yes" : "no") : "—"}
        testid="geocoding-google-ok"
      />
      <Row
        label="Resolver used"
        value={result ? (result.resolved_provider || (result.resolved_ok ? "—" : "unavailable")) : "—"}
        testid="geocoding-provider"
      />
      {result?.message && (
        <div
          className="text-[11px] mt-1 px-2 py-1 border"
          style={{
            backgroundColor: STATUS_COLORS[status].bg,
            borderColor: STATUS_COLORS[status].border,
            color: STATUS_COLORS[status].text,
          }}
          data-testid="geocoding-message"
        >
          {result.message}
        </div>
      )}
      {err && (
        <div className="text-[#EF4444] text-[11px] mt-1 break-all" data-testid="geocoding-error">
          {err}
        </div>
      )}
      <button
        type="button"
        onClick={runTest}
        disabled={running}
        className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-foreground text-background text-[11px] font-bold uppercase tracking-wider disabled:opacity-50"
        data-testid="geocoding-run-test-btn"
      >
        <RefreshCw className={`w-3 h-3 ${running ? "animate-spin" : ""}`} />
        {running ? "Testing…" : (result ? "Re-run test" : "Run test")}
      </button>
      {!result && !err && (
        <div className="text-muted-foreground text-[10px] mt-1">
          One-click check of the backend voice geocoding key. Never reveals the key value.
        </div>
      )}
    </SubsystemCard>
  );
};


// ─── Main panel ───────────────────────────────────────────────────────────

export const SystemHealthPanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);
  // Force re-render every second so "Xs ago" timers tick smoothly.
  const [, setTick] = useState(0);

  const fetchHealth = async () => {
    try {
      const res = await api.get("/admin/system-health");
      setData(res.data);
      setError(null);
      setLastFetchedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "fetch failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    pollRef.current = setInterval(fetchHealth, POLL_MS);
    const tickInt = setInterval(() => setTick((x) => x + 1), 1000);
    return () => {
      clearInterval(pollRef.current);
      clearInterval(tickInt);
    };
  }, []);

  const copySnapshot = async () => {
    if (!data) return;
    try {
      const text = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(text);
      toast.success("Health snapshot copied to clipboard");
    } catch (e) {
      toast.error("Couldn't copy — please select & copy manually");
    }
  };

  if (loading && !data) {
    return (
      <div className="p-8 text-center" data-testid="health-loading">
        <RefreshCw className="w-5 h-5 animate-spin inline-block text-muted-foreground" />
        <div className="text-sm text-muted-foreground mt-2">Loading system health…</div>
      </div>
    );
  }

  if (!data && error) {
    return (
      <div className="p-8 text-center" data-testid="health-error">
        <div className="border-2 border-[#EF4444] bg-[#FEF2F2] p-4 inline-block">
          <div className="label-mini text-[#991B1B]">SYSTEM HEALTH UNAVAILABLE</div>
          <div className="text-sm text-[#991B1B] mt-1">{error}</div>
          <button
            type="button"
            onClick={fetchHealth}
            className="mt-3 px-3 py-1.5 bg-[#EF4444] text-white text-xs font-bold uppercase tracking-wider"
            data-testid="health-retry-btn"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const overall = data?.status || "unknown";
  const subs = data?.subsystems || {};
  const overallColor = STATUS_COLORS[overall] || STATUS_COLORS.unknown;

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="system-health-panel">
      <BuildStamp />
      {/* Top banner — overall status, uptime, last-checked */}
      <div
        className="border-2 p-4 flex flex-col md:flex-row md:items-center gap-3"
        style={{ borderColor: overallColor.border, backgroundColor: overallColor.bg }}
        data-testid="health-overall-banner"
      >
        <div className="flex items-center gap-3">
          <StatusDot status={overall} size="w-4 h-4" />
          <div>
            <div className="label-mini" style={{ color: overallColor.text }}>OVERALL STATUS</div>
            <div className="font-heading text-xl font-black" style={{ color: overallColor.text }} data-testid="health-overall-label">
              {STATUS_LABEL[overall] || overall}
            </div>
          </div>
        </div>
        <div className="md:ml-auto flex flex-wrap gap-4 text-xs font-mono-tab">
          <div data-testid="health-uptime">
            <span className="text-muted-foreground uppercase text-[10px] tracking-wider mr-2">Uptime</span>
            <span className="font-bold">{formatUptime(data?.uptime_seconds)}</span>
          </div>
          <div data-testid="health-checked-at">
            <span className="text-muted-foreground uppercase text-[10px] tracking-wider mr-2">Last check</span>
            <span className="font-bold">{lastFetchedAt ? formatAgo(lastFetchedAt.toISOString()) : "—"}</span>
          </div>
          <button
            type="button"
            onClick={fetchHealth}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider hover:underline"
            data-testid="health-refresh-btn"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <button
            type="button"
            onClick={copySnapshot}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider hover:underline"
            data-testid="health-copy-snapshot-btn"
            disabled={!data}
          >
            <Copy className="w-3 h-3" /> Copy snapshot
          </button>
        </div>
      </div>

      {/* Subsystem grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="subsystem-grid">

        {/* Mongo */}
        <SubsystemCard id="mongo" title="MongoDB" icon={Database} status={subs.mongo?.status}>
          <Row label="Ping latency" value={subs.mongo?.latency_ms != null ? `${subs.mongo.latency_ms} ms` : "—"} testid="mongo-latency" />
          {subs.mongo?.error && (
            <div className="text-[#EF4444] text-[11px] mt-1 break-all" data-testid="mongo-error">
              {subs.mongo.error}
            </div>
          )}
        </SubsystemCard>

        {/* WebSockets */}
        <SubsystemCard id="ws" title="WebSockets" icon={Wifi} status={subs.websockets?.status}>
          <Row label="Total open" value={subs.websockets?.total ?? 0} testid="ws-total" />
          {Object.entries(subs.websockets?.by_role || {}).map(([role, count]) => (
            <Row key={role} label={role} value={count} testid={`ws-by-role-${role}`} />
          ))}
          {(!subs.websockets?.by_role || Object.keys(subs.websockets.by_role).length === 0) && (
            <div className="text-muted-foreground text-[11px]">No active connections</div>
          )}
        </SubsystemCard>

        {/* Janitor */}
        <SubsystemCard id="janitor" title="Janitor (self-heal)" icon={Repeat} status={subs.janitor?.status}>
          <Row label="Last sweep" value={subs.janitor?.last_sweep_age_seconds != null ? `${subs.janitor.last_sweep_age_seconds}s ago` : "—"} testid="janitor-last-sweep" />
          <Row label="Interval" value={subs.janitor?.interval_seconds ? `${subs.janitor.interval_seconds}s` : "—"} />
          <Row label="Sweeps total" value={subs.janitor?.sweeps_total ?? 0} testid="janitor-sweeps-total" />
          <Row label="Errors" value={subs.janitor?.errors_total ?? 0} testid="janitor-errors-total" />
          {subs.janitor?.last_error && (
            <div className="text-[#EF4444] text-[11px] mt-1 break-all" data-testid="janitor-last-error">
              {subs.janitor.last_error}
            </div>
          )}
          <div className="border-t border-border pt-1 mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            Last counts · rec {subs.janitor?.last_counts?.recovered ?? 0} · off {subs.janitor?.last_counts?.offlined ?? 0} · stk {subs.janitor?.last_counts?.stuck ?? 0} · clu {subs.janitor?.last_counts?.clusters ?? 0}
          </div>
        </SubsystemCard>

        {/* Dispatcher */}
        <SubsystemCard id="dispatcher" title="Dispatcher" icon={Send} status={subs.dispatcher?.status}>
          <Row label="In flight" value={subs.dispatcher?.in_flight ?? 0} testid="dispatcher-in-flight" />
          <Row label="Dispatching stuck" value={subs.dispatcher?.dispatching_stuck_count ?? 0} testid="dispatcher-dispatching-stuck" />
          <Row label="Offered stuck" value={subs.dispatcher?.offered_stuck_count ?? 0} testid="dispatcher-offered-stuck" />
          {(subs.dispatcher?.dispatching_stuck_sample?.length > 0 || subs.dispatcher?.offered_stuck_sample?.length > 0) && (
            <div className="border-t border-border pt-1 mt-1 text-[10px] text-muted-foreground" data-testid="dispatcher-stuck-sample">
              {subs.dispatcher.dispatching_stuck_sample?.map((id) => (
                <div key={id}>· dispatching {id.slice(0, 8)}</div>
              ))}
              {subs.dispatcher.offered_stuck_sample?.map((id) => (
                <div key={id}>· offered {id.slice(0, 8)}</div>
              ))}
            </div>
          )}
        </SubsystemCard>

        {/* Google */}
        <SubsystemCard id="google" title="Google Maps / Directions" icon={MapIcon} status={subs.google?.status}>
          <Row
            label="Maps JS key"
            value={subs.google?.maps_js_key_present ? (subs.google.maps_js_key_tail || "set") : "MISSING"}
            testid="google-maps-key"
          />
          <Row
            label="Directions key"
            value={subs.google?.directions_key_present ? (subs.google.directions_key_tail || "set") : "MISSING"}
            testid="google-directions-key"
          />
          <Row label="Calls (this process)" value={subs.google?.calls_total ?? 0} testid="google-calls-total" />
          <Row
            label="Cache hit rate"
            value={subs.google?.cache_hit_rate != null ? `${Math.round(subs.google.cache_hit_rate * 100)}%` : "—"}
            testid="google-cache-rate"
          />
          {subs.google?.last_success_at && (
            <Row label="Last success" value={formatAgo(subs.google.last_success_at)} />
          )}
          {subs.google?.last_error && (
            <div className="text-[#F59E0B] text-[11px] mt-1 break-all" data-testid="google-last-error">
              Last error: {subs.google.last_error}
              {subs.google.last_error_at && ` (${formatAgo(subs.google.last_error_at)})`}
            </div>
          )}
        </SubsystemCard>

        {/* Geocoding self-test (Voice line) — on-demand */}
        <GeocodingHealthCard />

        {/* Recent errors */}
        <SubsystemCard id="errors" title="Recent errors / warnings" icon={AlertTriangle} status={subs.errors?.status}>          <Row label="Recent count" value={subs.errors?.recent_count ?? 0} testid="errors-recent-count" />
          {subs.errors?.recent?.length > 0 ? (
            <div className="border-t border-border pt-2 mt-1 space-y-1.5 max-h-40 overflow-y-auto" data-testid="errors-recent-list">
              {subs.errors.recent.slice(0, 5).map((e, i) => (
                <div key={`${e.ts}-${e.logger}-${i}`} className="text-[11px]" data-testid={`error-row-${i}`}>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">{formatAgo(e.ts)}</span>
                    <span className={`px-1 py-0.5 text-[9px] font-bold ${e.level === "ERROR" || e.level === "CRITICAL" ? "bg-[#FEF2F2] text-[#991B1B]" : "bg-[#FFFBEB] text-[#92400E]"}`}>
                      {e.level}
                    </span>
                    <span className="text-muted-foreground truncate">{e.logger}</span>
                  </div>
                  <div className="ml-4 break-all opacity-80">{e.message}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground text-[11px]">No recent warnings or errors.</div>
          )}
        </SubsystemCard>

        {/* Notifications (email) */}
        <SubsystemCard id="notifications" title="Email notifications" icon={Mail} status={subs.notifications?.status}>
          <Row
            label="Resend key"
            value={subs.notifications?.key_present ? "configured" : "not set"}
            testid="notifications-key-present"
          />
          {subs.notifications?.from_address && (
            <Row label="From" value={subs.notifications.from_address} testid="notifications-from" />
          )}
          <Row label="Last hour total" value={subs.notifications?.last_hour_total ?? 0} testid="notifications-last-hour-total" />
          {Object.entries(subs.notifications?.last_hour_counts || {}).map(([s, n]) => (
            <Row key={s} label={s} value={n} testid={`notifications-status-${s}`} />
          ))}
          {!subs.notifications?.key_present && (
            <div className="text-[10px] text-[#92400E] bg-[#FFFBEB] border border-[#FFC107] px-2 py-1 mt-1" data-testid="notifications-nokey-banner">
              No-op mode — set RESEND_API_KEY to enable sending. Audit rows are still recorded.
            </div>
          )}
        </SubsystemCard>
      </div>

      {error && data && (
        <div className="border border-[#FFC107] bg-[#FFFBEB] p-2 text-xs text-[#92400E]" data-testid="health-stale-warning">
          Last poll failed: {error} (showing previous snapshot)
        </div>
      )}
    </div>
  );
};
export default SystemHealthPanel;
